import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ConcealmentRequest from "@/components/ConcealmentRequest";
import { API } from "@/auth/AuthContext";
import { inputCls, labelCls } from "@/components/formkit";

const PACKAGES = [
  { id: "install", label: "Installation Only", desc: "You provide a compatible mount" },
  { id: "flat", label: "Flat Mount Included", desc: "Low-profile fixed mount" },
  { id: "tilting", label: "Tilting Mount Included", desc: "Angle adjustment for glare" },
  { id: "full_motion", label: "Full-Motion Included", desc: "Extend, tilt and swivel" },
];

const WALL_TYPES = ["Drywall with wood studs", "Drywall with metal studs", "Brick", "Concrete", "Stone", "Above fireplace", "Not sure"];
const MOUNT_OPTIONS = [["yes", "Yes"], ["no", "No"], ["help", "Need help choosing"]];
const EQUIPMENT = ["Soundbar", "Cable box", "Streaming device", "Gaming console", "Modem", "Router", "Coaxial connection", "Ethernet connection", "Other"];
const COMPLEX_EQUIPMENT = ["Coaxial connection", "Ethernet connection"];

const CHECKLIST = [
  "Have your television and equipment available.",
  "Have your mounting bracket ready if providing your own.",
  "Make sure the installation area is accessible.",
  "Have necessary cables and remote controls available.",
  "Notify us about unusual wall conditions.",
  "Let us know about additional installation requests.",
  "Clear furniture and obstacles from the work area.",
];

const PHOTO_SUGGESTIONS = [
  "Full picture of the installation wall.",
  "Existing electrical outlets and cable connections.",
  "Equipment being installed.",
  "Surrounding installation area.",
  "Additional details or unusual conditions.",
];

function Stepper({ step }) {
  const steps = ["TV Size", "Package", "Concealment & Gear", "Details & Photos", "Appointment", "Review"];
  return (
    <div className="flex flex-wrap gap-1.5 mb-10 justify-center" data-testid="booking-stepper">
      {steps.map((s, i) => (
        <span key={s} className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${
          i + 1 === step ? "bg-[#d4af37] text-black font-bold"
            : i + 1 < step ? "bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30"
            : "bg-[#121216] text-zinc-500 border border-zinc-800"}`}>
          {i + 1}. {s}
        </span>
      ))}
    </div>
  );
}

function DepositPayPal({ booking, onPaid }) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios.get(`${API}/paypal/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);
  if (!config?.enabled) return <p className="text-xs text-red-400 font-mono">Payments unavailable — call 469-897-6497.</p>;
  return (
    <div data-testid="deposit-paypal">
      <PayPalScriptProvider options={{ clientId: config.client_id, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 40 }}
          createOrder={async () => {
            const { data } = await axios.post(`${API}/bookings/${booking.id}/deposit-order`);
            return data.order_id;
          }}
          onApprove={async (d) => {
            try {
              const { data } = await axios.post(`${API}/bookings/${booking.id}/capture`, { order_id: d.orderID });
              if (data.status === "confirmed") onPaid();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Payment could not be confirmed.");
            }
          }}
          onError={() => toast.error("PayPal error. Your booking is saved — you can call us to pay the deposit.")}
        />
      </PayPalScriptProvider>
    </div>
  );
}

function concealPriceLabel(o) {
  if (o.kind === "custom") {
    if (o.display === "range") return `$${o.price}–$${o.price_max}+`;
    return o.key === "outlet" ? "Licensed Electrician Quote" : "Estimate Required";
  }
  return o.display === "starting" ? `Starting at $${o.price}` : `+$${o.price}`;
}

const fmt12 = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const windowLabel = (start, mins) => {
  const [h, m] = start.split(":").map(Number);
  const endTotal = h * 60 + m + mins;
  const end = `${String(Math.floor(endTotal / 60)).padStart(2, "0")}:${String(endTotal % 60).padStart(2, "0")}`;
  return `${fmt12(start)} – ${fmt12(end)}`;
};

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const promoKey = searchParams.get("promo") || null;
  const promoSize = searchParams.get("size") || null;
  const [step, setStep] = useState(1);
  const [settings, setSettings] = useState(null);
  const [payChoice, setPayChoice] = useState("pay_after");
  const [addons, setAddons] = useState([]);
  const [size, setSize] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [wallType, setWallType] = useState("");
  const [hasMount, setHasMount] = useState("");
  const [equipment, setEquipment] = useState([]);
  const [concealment, setConcealment] = useState("none");
  const [pickedAddons, setPickedAddons] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dt28_customer") || "null"); } catch { return null; }
  });

  // Verify any Google session server-side (cookie is source of truth) and pre-fill the details form
  useEffect(() => {
    axios.get(`${API}/auth/profile`, { withCredentials: true })
      .then(({ data }) => {
        localStorage.setItem("dt28_customer", JSON.stringify(data));
        setCustomer(data);
        setForm((f) => ({
          ...f,
          name: f.name || data.name || "",
          email: f.email || data.email || "",
        }));
      })
      .catch(() => {
        localStorage.removeItem("dt28_customer");
        setCustomer(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [days, setDays] = useState(null);

  useEffect(() => {
    axios.get(`${API}/availability/days`).then((r) => setDays(r.data.days || [])).catch(() => setDays([]));
  }, []);
  const [time, setTime] = useState("");
  const [ack, setAck] = useState(false);
  const [booking, setBooking] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) =>
      setAddons(r.data.filter((s) => (s.price_type === "fixed" || s.price_type === "per_unit") && !s.key.startsWith("tv-mount-") && !s.key.startsWith("mount-") && !s.key.startsWith("wire-")))
    ).catch(() => null);
    axios.get(`${API}/booking-options`).then((r) => setSettings(r.data)).catch(() => toast.error("Could not load booking options."));
  }, []);

  const promo = promoKey && settings?.promos?.[promoKey] ? settings.promos[promoKey] : null;
  const promoPrice = promo ? (promo.size_prices ? promo.size_prices[size] : promo.price) : null;
  const promoStarting = Boolean(promo?.starting);

  // Scroll to top on every step change so step content never hides under the fixed header
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [step]);

  // Promotion mode: pre-select the promo's included configuration once options load
  useEffect(() => {
    if (!promo) return;
    const validSize = promoSize && settings.tv_packages.some((t) => t.key === promoSize);
    setSize((s) => s || (validSize ? promoSize : (promo.size_prices ? "tv-51-64" : "tv-65-75")));
    setPkg((p) => p || "flat");
    setConcealment(promoKey === "tv-concealment" ? "inwall" : "none");
    setHasMount((h) => h || "No");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo]);

  useEffect(() => {
    if (!date) return;
    setSlots(null);
    setTime("");
    axios.get(`${API}/availability/slots?date=${date}`)
      .then((r) => setSlots(r.data))
      .catch(() => setSlots({ slots: [] }));
  }, [date]);

  const tier = settings?.tv_packages?.find((t) => t.key === size);
  const price = tier && pkg ? tier.prices[pkg] : 0;
  const concealOpt = settings?.concealment_options?.find((o) => o.key === concealment);
  const concealmentPrice = concealOpt && concealOpt.kind === "fixed" ? concealOpt.price : 0;
  const addonsTotal = useMemo(
    () => pickedAddons.reduce((s, k) => s + (addons.find((a) => a.key === k)?.price || 0), 0),
    [pickedAddons, addons]);
  const total = promo ? (promoPrice || 0) : (price || 0) + addonsTotal + concealmentPrice;

  const complexReasons = useMemo(() => {
    const r = [];
    if (wallType === "Above fireplace") r.push("Above-fireplace installation");
    if (equipment.some((e) => COMPLEX_EQUIPMENT.includes(e))) r.push("New coaxial / Ethernet cable runs");
    if (concealOpt?.kind === "custom") r.push(`Custom concealment (${concealOpt.name})`);
    return r;
  }, [wallType, equipment, concealOpt]);
  const estimateRequired = !promo && complexReasons.length > 0;
  const photosRecommended = estimateRequired || equipment.length >= 2 || wallType === "Not sure";

  const deposit = useMemo(() => {
    if (promo) return (payChoice === "pay_now" && !promoStarting) ? (promoPrice || 0) : 0;
    if (estimateRequired || !settings || !pkg || pkg === "install") return 0;
    if (tier?.mount_included && pkg === "flat") return settings.deposits.standard_large;
    return settings.deposits[pkg] || 0;
  }, [settings, pkg, tier, estimateRequired, promo, payChoice, promoPrice, promoStarting]);
  const balance = total - deposit;

  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, 5 - photos.length)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/uploads`, fd);
        setPhotos((p) => [...p, data]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const confirmBooking = async () => {
    if (busy) return;
    if (!ack) {
      toast.error("Please acknowledge the installation pricing policy.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        tv_size_key: size, package: pkg,
        wall_type: wallType, has_mount: hasMount, equipment,
        concealment,
        addons: pickedAddons,
        photo_ids: photos.map((p) => p.id),
        date, time,
        approval_ack: ack,
        ...(promo ? { promo: promoKey, payment_method: payChoice } : {}),
      });
      setBooking(data);
      if (data.estimate_required || data.deposit_due === 0) setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create booking.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleEquipment = (e) => setEquipment((p) => p.includes(e) ? p.filter((x) => x !== e) : [...p, e]);
  const nextDisabled =
    (step === 1 && !size) || (step === 2 && (!pkg || !wallType || !hasMount)) ||
    (step === 4 && (!form.name || !form.email || !form.phone || !form.address)) ||
    (step === 5 && (!date || !time));

  if (done && booking) {
    return (
      <>
        <Nav />
        <main className="max-w-2xl mx-auto px-6 py-20" data-testid="booking-success">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-[#d4af37]/50 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#d4af37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <h1 className="font-display text-4xl uppercase text-white">Thank You for Choosing DuroTech28!</h1>
            <p className="text-sm text-zinc-300 mt-4 leading-relaxed">
              We've received your installation request{booking.estimate_required ? "" : ` for ${booking.date} at ${booking.time}`}.
              Our team will review your selected services and any uploaded photos.
            </p>
            {booking.estimate_required ? (
              <p className="text-xs text-amber-400 font-mono mt-4 leading-relaxed">
                Your installation needs a custom estimate ({booking.complex_reasons?.join("; ")}).
                We'll contact you with final pricing before confirming the installation — nothing is charged yet.
              </p>
            ) : (
              <p className="text-xs text-zinc-500 font-mono mt-4 leading-relaxed">
                {booking.deposit_due > 0
                  ? (booking.payment_method === "pay_now"
                      ? `Paid online $${booking.deposit_due} · nothing due after installation. `
                      : `Deposit paid $${booking.deposit_due} · Balance $${booking.balance_due} due after installation. `)
                  : `Total $${booking.total?.toLocaleString()} · payment due after installation. `}
                If additional information or a custom estimate is required, we'll contact you before confirming.
              </p>
            )}
            <p className="text-xs text-zinc-500 font-mono mt-4">Thank you for trusting DuroTech28 with your technology installation needs.</p>
          </div>
          {booking.concealment_quote_required && (
            <ConcealmentRequest prefill={form} bookingId={booking.id} />
          )}
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Seo
        title="Book an Installation | DuroTech28 — Dallas–Fort Worth"
        description="Book your TV mounting or installation appointment online. Live availability, transparent pricing and flexible payment options across Dallas–Fort Worth."
        canonical="https://durotech28.me/book"
      />
      <Nav />
      <main className="max-w-3xl mx-auto px-6 py-14" data-testid="booking-page">
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-widest text-[#d4af37] font-mono">TV Mounting · Dallas–Fort Worth</span>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white mt-2">Book Your Installation</h1>
        </div>

        <Stepper step={step} />

        {promo && (
          <div className="mb-6 rounded-xl border border-[#d4af37]/50 bg-[#d4af37]/10 p-4 text-center" data-testid="promo-banner">
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">Promotion applied</p>
            <p className="text-sm font-semibold text-white mt-1">{promo.label}</p>
            {promoPrice != null && (
              <p className="font-display text-2xl text-[#d4af37] mt-1" data-testid="promo-price">
                {promoStarting ? `Starting at $${promoPrice.toLocaleString()}` : `$${promoPrice.toLocaleString()}`}
              </p>
            )}
            <p className="text-[10px] font-mono text-zinc-400 mt-1">
              {promoStarting
                ? "Final quote confirmed after consultation · $0 today"
                : "FREE standard flat mount included · pay now online or after installation"}
            </p>
          </div>
        )}

        {!settings ? (
          <p className="text-zinc-500 font-mono text-xs text-center">Loading...</p>
        ) : (
          <div className="rounded-2xl p-6 sm:p-10 bg-[#121216] gold-border">
            {/* STEP 1 — size */}
            {step === 1 && (
              <div data-testid="step-size">
                <h2 className="font-display text-2xl uppercase text-white mb-6">What size is your TV?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {settings.tv_packages.map((t) => (
                    <button key={t.key} type="button" onClick={() => { setSize(t.key); setPkg(null); }}
                      data-testid={`size-${t.key}`}
                      className={`rounded-xl p-5 text-left border transition-colors ${
                        size === t.key ? "bg-[#d4af37]/10 border-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 hover:border-zinc-500"}`}>
                      <p className="font-display text-xl text-white">{t.label}</p>
                      <p className="text-xs font-mono text-[#d4af37] mt-1">from ${t.prices.install}</p>
                      {t.mount_included && <p className="text-[10px] font-mono text-zinc-500 mt-1">Heavy-duty mount included</p>}
                    </button>
                  ))}
                  <a href="/services" data-testid="size-over-100"
                    className="rounded-xl p-5 text-left border border-zinc-800 bg-[#0a0a0c] hover:border-[#d4af37]/40 transition-colors sm:col-span-2">
                    <p className="font-display text-xl text-zinc-300">Over 100"</p>
                    <p className="text-xs font-mono text-zinc-500 mt-1">Custom quote required — request it here →</p>
                  </a>
                </div>
              </div>
            )}

            {/* STEP 2 — package + wall + mount */}
            {step === 2 && tier && (
              <div data-testid="step-package">
                <h2 className="font-display text-2xl uppercase text-white mb-2">Choose your package</h2>
                <p className="text-xs text-zinc-500 font-mono mb-6">TV size: {tier.label}
                  {tier.mount_included && " · a standard compatible heavy-duty flat mount is already included at this size"}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PACKAGES.map((p) => (
                    <button key={p.id} type="button" onClick={() => setPkg(p.id)}
                      data-testid={`pkg-${p.id}`}
                      className={`rounded-xl p-5 text-left border transition-colors ${
                        pkg === p.id ? "bg-[#d4af37]/10 border-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 hover:border-zinc-500"}`}>
                      <p className="text-sm font-semibold text-white">{p.label}</p>
                      <p className="text-[10px] font-mono text-zinc-500 mt-1">{p.desc}</p>
                      <p className="font-display text-2xl text-[#d4af37] mt-2">${tier.prices[p.id]}</p>
                      {tier.mount_included && p.id === "flat" && (
                        <p className="text-[9px] font-mono text-emerald-400 mt-1">Mount included — no extra charge</p>)}
                      {tier.mount_included && (p.id === "tilting" || p.id === "full_motion") && (
                        <p className="text-[9px] font-mono text-zinc-500 mt-1">Upgrade difference only</p>)}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 font-mono mt-4">Mounts are matched to your TV's weight and VESA mounting requirements.</p>

                <div className="border-t border-zinc-800 mt-8 pt-6 space-y-6">
                  <div>
                    <span className={labelCls}>What type of wall? *</span>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {WALL_TYPES.map((w) => (
                        <button key={w} type="button" onClick={() => setWallType(w)} data-testid={`wall-${w.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`px-3 py-2.5 rounded border text-left text-[11px] font-mono transition-colors ${
                            wallType === w ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                          {w}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className={labelCls}>Do you have a TV mount? *</span>
                    <div className="flex flex-wrap gap-2">
                      {MOUNT_OPTIONS.map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setHasMount(v)} data-testid={`mount-${v}`}
                          className={`px-4 py-2.5 rounded border text-[11px] font-mono transition-colors ${
                            hasMount === v ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 — concealment + equipment */}
            {step === 3 && (
              <div data-testid="step-addons">
                <h2 className="font-display text-2xl uppercase text-white mb-2">Do you need cable concealment?</h2>
                <div className="grid sm:grid-cols-2 gap-2 mb-3 mt-4">
                  <button type="button" onClick={() => setConcealment("none")} data-testid="conceal-none"
                    className={`rounded-lg px-4 py-3 text-left border transition-colors ${concealment === "none" ? "bg-[#d4af37]/10 border-[#d4af37]" : "bg-[#0a0a0c] border-zinc-800 hover:border-zinc-600"}`}>
                    <p className="text-xs text-zinc-200">No Cable Concealment</p>
                    <p className="text-[10px] font-mono text-zinc-500 mt-0.5">$0</p>
                  </button>
                  {settings.concealment_options.map((o) => (
                    <div key={o.key}
                      className={`rounded-lg border transition-colors ${concealment === o.key ? "bg-[#d4af37]/10 border-[#d4af37]" : "bg-[#0a0a0c] border-zinc-800"}`}>
                      <button type="button" onClick={() => setConcealment(o.key)} data-testid={`conceal-${o.key}`}
                        className="w-full px-4 py-3 text-left">
                        <p className="text-xs text-zinc-200">{o.name}</p>
                        <p className={`text-[10px] font-mono mt-0.5 ${o.kind === "custom" ? "text-amber-400" : "text-[#d4af37]"}`}>
                          {concealPriceLabel(o)}
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">{o.desc}</p>
                      </button>
                      {o.included && (
                        <div className="px-4 pb-3 space-y-1" data-testid={`conceal-details-${o.key}`}>
                          {[["What's Included", o.included], ["What's Not Included", o.not_included], ["Installation Requirements", o.requirements]].map(([t, list]) => (
                            <details key={t} className="text-[10px] font-mono">
                              <summary className="text-[#d4af37] cursor-pointer py-1 select-none">{t}</summary>
                              <ul className="pl-4 pt-1 space-y-1 text-zinc-400">
                                {list.map((i) => <li key={i}>· {i}</li>)}
                              </ul>
                            </details>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 font-mono leading-relaxed mb-8 border-l-2 border-[#d4af37]/30 pl-3">
                  Standard in-wall concealment covers appropriate low-voltage cabling only. Standard television power
                  cords and ordinary extension cords cannot be concealed inside walls. Where a new electrical outlet,
                  wiring, or circuit modification is required, the work is performed by an appropriately licensed
                  electrician — DuroTech28 coordinates with licensed electrical partners.
                </p>

                <h3 className="font-display text-lg uppercase text-white mb-2">Do you need additional equipment installed?</h3>
                <div className="flex flex-wrap gap-2 mb-2 mt-3">
                  {EQUIPMENT.map((e) => (
                    <button key={e} type="button" onClick={() => toggleEquipment(e)} data-testid={`equip-${e.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`px-3 py-2 rounded border text-[11px] font-mono transition-colors ${
                        equipment.includes(e) ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                      {equipment.includes(e) ? "✓ " : ""}{e}
                    </button>
                  ))}
                </div>
                {(equipment.length >= 2 || estimateRequired) && (
                  <p className="text-[10px] font-mono text-amber-400 mb-4" data-testid="complex-hint">
                    Multiple devices or advanced options — please describe your setup in the notes and upload photos on the next step so we can prepare an accurate estimate.
                  </p>
                )}

                <h3 className="font-display text-lg uppercase text-white mb-4 mt-8">Add more services to the same visit</h3>
                <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                  {addons.map((a) => {
                    const active = pickedAddons.includes(a.key);
                    return (
                      <button key={a.key} type="button"
                        onClick={() => setPickedAddons((p) => active ? p.filter((k) => k !== a.key) : [...p, a.key])}
                        data-testid={`addon-${a.key}`}
                        className={`rounded-lg px-4 py-3 text-left border transition-colors ${
                          active ? "bg-[#d4af37]/10 border-[#d4af37]" : "bg-[#0a0a0c] border-zinc-800 hover:border-zinc-600"}`}>
                        <p className="text-xs text-zinc-200">{a.name}</p>
                        <p className="text-[10px] font-mono text-[#d4af37] mt-0.5">+${a.price}{a.unit ? ` ${a.unit}` : ""}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 4 — details + photos */}
            {step === 4 && (
              <div className="space-y-4" data-testid="step-details">
                <h2 className="font-display text-2xl uppercase text-white mb-2">Your details</h2>
                {customer ? (
                  <div className="flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid="customer-signed-in">
                    <p className="text-[11px] font-mono text-emerald-300">Signed in as {customer.name || customer.email} — details pre-filled</p>
                    <button type="button" data-testid="customer-signout"
                      onClick={() => { localStorage.removeItem("dt28_customer"); setCustomer(null); }}
                      className="text-[10px] font-mono text-zinc-500 hover:text-zinc-300 uppercase tracking-wider">Sign out</button>
                  </div>
                ) : (
                  <button type="button" data-testid="customer-google-btn"
                    onClick={() => {
                      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                      const redirectUrl = window.location.origin + '/book';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    className="w-full py-2.5 rounded border border-zinc-700 text-zinc-300 text-[10px] font-mono uppercase tracking-wider hover:border-[#d4af37] hover:text-[#d4af37] transition-colors flex items-center justify-center gap-2">
                    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.4.75 3.44 3.4 1.53 7.26l3.66 2.84C6.06 7.09 8.8 5.04 12 5.04z"/><path fill="#4285F4" d="M23.25 12.26c0-.79-.07-1.54-.2-2.26H12v4.51h6.32c-.27 1.48-1.11 2.72-2.37 3.56l3.62 2.81c2.12-1.96 3.68-4.85 3.68-8.62z" transform="translate(-0.5 0)"/><path fill="#FBBC05" d="M5.19 14.29a7.2 7.2 0 0 1 0-4.58L1.53 6.87a11.26 11.26 0 0 0 0 10.26l3.66-2.84z"/><path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.62-2.81c-1 .68-2.29 1.08-3.84 1.08-3.2 0-5.94-2.05-6.81-4.9l-3.66 2.84C3.44 20.6 7.4 23.25 12 23.25z"/></svg>
                    Sign in with Google to auto-fill your details
                  </button>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Full Name *</label>
                    <input value={form.name} onChange={set("name")} data-testid="book-name" className={inputCls} /></div>
                  <div><label className={labelCls}>Email *</label>
                    <input type="email" value={form.email} onChange={set("email")} data-testid="book-email" className={inputCls} /></div>
                  <div><label className={labelCls}>Phone *</label>
                    <input value={form.phone} onChange={set("phone")} data-testid="book-phone" className={inputCls} /></div>
                  <div><label className={labelCls}>Service Address *</label>
                    <input value={form.address} onChange={set("address")} data-testid="book-address" className={inputCls} /></div>
                </div>
                <div><label className={labelCls}>Notes (wall conditions, equipment, special requests)</label>
                  <textarea rows="3" value={form.notes} onChange={set("notes")} data-testid="book-notes" className={inputCls}></textarea></div>

                <div className="rounded-xl border border-[#d4af37]/25 bg-[#0d0d10] p-4">
                  <h3 className="font-display text-base uppercase text-white">Help Us Prepare for Your Installation</h3>
                  <p className="text-[11px] text-zinc-400 font-mono mt-1.5 leading-relaxed">
                    Upload pictures of your installation area so our technicians can evaluate your project,
                    identify potential challenges, and provide a more accurate estimate.
                    {photosRecommended
                      ? " Strongly recommended for your installation."
                      : " Optional for basic installations."}
                  </p>
                  <ul className="text-[10px] font-mono text-zinc-500 mt-3 space-y-1">
                    {PHOTO_SUGGESTIONS.map((s, i) => <li key={s}>{i + 1}. {s}</li>)}
                  </ul>
                  <label className="mt-3 flex items-center justify-center w-full border border-dashed border-zinc-600 hover:border-[#d4af37] rounded p-4 text-[11px] font-mono text-zinc-400 cursor-pointer transition-colors" data-testid="book-photos">
                    <input type="file" accept="image/jpeg,image/png,image/heic,image/webp" multiple className="hidden" disabled={uploading}
                      onChange={(e) => uploadPhotos(e.target.files)} />
                    {uploading ? "Uploading..." : photos.length >= 5 ? "5 photos attached (max)" : "Tap to attach photos (JPG, PNG, HEIC — up to 5)"}
                  </label>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mt-3" data-testid="photo-previews">
                      {photos.map((p) => (
                        <div key={p.id} className="relative group">
                          <img src={`${API}/files/${p.id}?key=preview`} alt=""
                            onError={(e) => { e.target.style.display = "none"; }}
                            className="w-full aspect-square object-cover rounded border border-zinc-700" />
                          <div className="w-full aspect-square rounded border border-zinc-700 bg-[#121216] flex items-center justify-center text-[8px] font-mono text-zinc-500 -mt-[100%] relative -z-10">attached</div>
                          <button type="button" onClick={() => setPhotos((x) => x.filter((y) => y.id !== p.id))}
                            data-testid={`photo-remove-${p.id}`}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] leading-none">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 5 — appointment */}
            {step === 5 && (
              <div data-testid="step-appointment">
                <h2 className="font-display text-2xl uppercase text-white mb-6">Pick your appointment</h2>
                <label className={labelCls}>Date</label>
                <input type="hidden" data-testid="book-date" value={date} readOnly />
                {days === null ? (
                  <p className="text-xs font-mono text-zinc-500 mb-5">Loading available days...</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-5" data-testid="day-grid">
                    {days.map((d) => (
                      <button key={d.date} type="button" disabled={!d.open}
                        onClick={() => { setDate(d.date); setTime(""); }}
                        data-testid={`day-${d.date}`}
                        className={`py-2.5 px-1 rounded border text-center transition-colors ${
                          date === d.date
                            ? "bg-[#d4af37] text-black border-[#d4af37] font-bold"
                            : d.open
                              ? "bg-[#0a0a0c] border-zinc-700 text-zinc-300 hover:border-[#d4af37]"
                              : "bg-[#0a0a0c] border-zinc-800/60 text-zinc-600 cursor-not-allowed"}`}>
                        <span className="block text-[8px] font-mono uppercase tracking-wider opacity-70">{d.dow}</span>
                        <span className="block text-sm font-mono font-bold leading-tight">{d.day}</span>
                        <span className="block text-[8px] font-mono uppercase tracking-wider opacity-70">{d.open ? d.month : "Closed"}</span>
                      </button>
                    ))}
                  </div>
                )}
                {date && (
                  slots === null ? <p className="text-xs font-mono text-zinc-500">Checking availability...</p>
                  : !slots.accepting_bookings ? (
                    <p className="text-xs font-mono text-amber-400" data-testid="bookings-paused">
                      Online booking is temporarily paused — you can still request a quote and we'll schedule you by phone.
                    </p>
                  ) : slots.slots.length === 0 ? (
                    <p className="text-xs font-mono text-zinc-500" data-testid="no-slots">No openings that day — try another date.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="slot-grid">
                      {slots.slots.map((s) => (
                        <button key={s} type="button" onClick={() => setTime(s)}
                          data-testid={`slot-${s.replace(":", "")}`}
                          className={`py-3.5 rounded border font-mono transition-colors ${
                            time === s ? "bg-[#d4af37] text-black font-bold border-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 text-zinc-300 hover:border-[#d4af37]"}`}>
                          <span className="block text-xs font-bold">{windowLabel(s, slots.window_minutes || 180)}</span>
                          <span className="block text-[8px] uppercase tracking-widest opacity-70 mt-0.5">3-hour window</span>
                        </button>
                      ))}
                    </div>
                  )
                )}
                {estimateRequired && (
                  <p className="text-[10px] font-mono text-amber-400 mt-4 leading-relaxed">
                    Your project needs a custom estimate — this appointment is a preference, and we'll confirm
                    timing and final pricing with you before anything is scheduled.
                  </p>
                )}
              </div>
            )}

            {/* STEP 6 — review */}
            {step === 6 && (
              <div data-testid="step-review">
                <h2 className="font-display text-2xl uppercase text-white mb-6">Review &amp; confirm</h2>

                <div className="rounded-xl bg-[#0d0d10] border border-[#d4af37]/25 p-5 mb-4">
                  <p className="font-display text-sm uppercase text-[#d4af37] tracking-wide">DuroTech28 Installation Standard</p>
                  <p className="text-[10px] font-mono text-zinc-400 mt-1">Professional Workmanship. Transparent Pricing. No Shortcuts.</p>
                  <p className="text-[11px] text-zinc-400 leading-relaxed mt-3">
                    We don't cut corners to compete on price. Whether we're mounting a television, installing a smart
                    home system, or building commercial technology infrastructure, we approach every project with
                    professionalism and care.
                  </p>
                  <p className="text-[11px] font-mono text-white mt-2 italic">"Do it right. Do it safely. Stand behind your work."</p>
                </div>

                <div className="rounded-xl bg-[#0a0a0c] border border-zinc-800 p-5 space-y-2 text-xs font-mono">
                  {promo ? (
                    <div className="flex justify-between"><span className="text-zinc-400">{promo.label} — {tier.label}</span><span className="text-[#d4af37] font-bold" data-testid="review-promo-line">{promoStarting ? `Starting at $${total.toLocaleString()}` : `$${total.toLocaleString()}`}</span></div>
                  ) : (
                    <div className="flex justify-between"><span className="text-zinc-400">TV Mounting — {tier.label}</span><span className="text-zinc-200">{PACKAGES.find(p => p.id === pkg)?.label} · ${price}</span></div>
                  )}
                  {promo && !promoStarting && <p className="text-[10px] text-emerald-400">FREE standard flat mount included in your promotion</p>}
                  {promoStarting && <p className="text-[10px] text-zinc-500">Labor starting price — equipment and materials separate. Final quote confirmed at your consultation.</p>}
                  {tier.mount_included && pkg === "flat" && <p className="text-[10px] text-emerald-400">Standard heavy-duty mount included — not charged twice</p>}
                  <div className="flex justify-between"><span className="text-zinc-400">Wall type</span><span className="text-zinc-200">{wallType}</span></div>
                  {equipment.length > 0 && <div className="flex justify-between"><span className="text-zinc-400">Equipment</span><span className="text-zinc-200 text-right">{equipment.join(", ")}</span></div>}
                  {concealOpt && concealment !== "none" && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">{concealOpt.name}</span>
                      {promo ? <span className="text-emerald-400">Included</span> : (
                        <span className={concealOpt.kind === "custom" ? "text-amber-400" : "text-zinc-200"}>
                          {concealOpt.kind === "custom" ? concealPriceLabel(concealOpt) : `+$${concealOpt.price}`}
                        </span>
                      )}
                    </div>
                  )}
                  {!promo && pickedAddons.map((k) => {
                    const a = addons.find((x) => x.key === k);
                    return a && <div key={k} className="flex justify-between"><span className="text-zinc-400">{a.name}</span><span className="text-zinc-200">+${a.price}</span></div>;
                  })}
                  {promo && pickedAddons.length > 0 && (
                    <p className="text-[10px] text-zinc-500">Requested extras beyond the promotion are quoted and approved before any additional charge.</p>
                  )}
                  <div className="border-t border-zinc-800 my-3"></div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white">{estimateRequired ? "Estimated total" : "Total price"}</span>
                    <span className="text-[#d4af37] font-bold" data-testid="review-total">${total.toLocaleString()}</span>
                  </div>
                  {estimateRequired ? (
                    <p className="text-[10px] text-amber-400/90" data-testid="estimate-required-note">
                      Custom Estimate Required ({complexReasons.join("; ")}). Your request goes to review —
                      final pricing is confirmed with you before any work or payment.
                    </p>
                  ) : promo ? (
                    <div className="pt-2" data-testid="promo-payment-choice">
                      {promoStarting ? (
                        <div className="rounded-lg border border-[#d4af37]/40 bg-[#d4af37]/5 px-4 py-3" data-testid="consultation-note">
                          <p className="text-xs font-bold text-white">Reserve Your Consultation — $0 Today</p>
                          <p className="text-[10px] text-zinc-400 mt-0.5">Your 3-hour appointment window is reserved now. Final quote confirmed at the consultation; nothing is charged upfront.</p>
                        </div>
                      ) : (
                        <>
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Choose how to pay</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button type="button" onClick={() => setPayChoice("pay_after")} data-testid="pay-after-btn"
                          className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                            payChoice === "pay_after" ? "border-[#d4af37] bg-[#d4af37]/10" : "border-zinc-700 bg-[#0a0a0c] hover:border-zinc-500"}`}>
                          <p className="text-xs font-bold text-white">Pay After Installation</p>
                          <p className="text-[10px] text-zinc-400 mt-0.5">$0 today — pay ${total.toLocaleString()} when the job is done</p>
                        </button>
                        <button type="button" onClick={() => setPayChoice("pay_now")} data-testid="pay-now-btn"
                          className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                            payChoice === "pay_now" ? "border-[#d4af37] bg-[#d4af37]/10" : "border-zinc-700 bg-[#0a0a0c] hover:border-zinc-500"}`}>
                          <p className="text-xs font-bold text-white">Pay Now</p>
                          <p className="text-[10px] text-zinc-400 mt-0.5">${total.toLocaleString()} securely online via PayPal</p>
                        </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between"><span className="text-zinc-400">Deposit due today</span><span className="text-[#d4af37]" data-testid="review-deposit">${deposit.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-400">Balance after installation</span><span className="text-zinc-200" data-testid="review-balance">${balance.toLocaleString()}</span></div>
                    </>
                  )}
                  <div className="flex justify-between"><span className="text-zinc-400">Appointment</span><span className="text-zinc-200">{date} · {windowLabel(time, 180)}</span></div>
                  {photos.length > 0 && <div className="flex justify-between"><span className="text-zinc-400">Photos attached</span><span className="text-zinc-200">{photos.length}</span></div>}
                </div>

                <div className="rounded-xl bg-[#0a0a0c] border border-zinc-800 p-5 mt-4">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-2">Before your technician arrives</p>
                  <ul className="text-[11px] font-mono text-zinc-400 space-y-1">
                    {CHECKLIST.map((c) => <li key={c}>· {c}</li>)}
                  </ul>
                </div>

                <div className="rounded-xl bg-[#0a0a0c] border border-zinc-800 p-5 mt-4">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-2">Installation Pricing Policy</p>
                  <p className="text-[11px] font-mono text-zinc-400 leading-relaxed">
                    Our listed prices cover the services described in your booking. Additional materials, installation
                    requirements, or work outside the original scope may result in additional charges. If additional
                    work is necessary, your technician will explain the options and provide an updated estimate before
                    proceeding. No additional work will be performed without customer approval.
                  </p>
                  <p className="text-[10px] font-mono text-zinc-500 mt-3">{settings.cancellation_policy}</p>
                  {promoKey === "tv-concealment" && (
                    <p className="text-[10px] font-mono text-amber-400/90 mt-3 leading-relaxed" data-testid="promo-disclosure">
                      Standard in-wall wire concealment is included. Additional charges of up to $350 may apply for
                      custom wire routing, outlet location issues, or wall obstructions. Additional costs are
                      determined on-site and require customer approval.
                    </p>
                  )}
                  <label className="flex items-start gap-2.5 mt-4 cursor-pointer">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                      data-testid="policy-ack" className="accent-[#d4af37] mt-0.5" />
                    <span className="text-[11px] font-mono text-zinc-300">
                      I understand that additional services or materials may require an updated estimate and my approval.
                    </span>
                  </label>
                </div>

                {!booking ? (
                  <button type="button" onClick={confirmBooking} disabled={busy || !ack} data-testid="book-confirm-btn"
                    className="mt-6 w-full py-4 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity disabled:opacity-60">
                    {busy ? "Submitting..." : estimateRequired ? "Submit for Custom Estimate"
                      : promo ? (payChoice === "pay_now" ? `Confirm & Pay $${total.toLocaleString()} Online` : "Confirm Booking — $0 Today")
                      : deposit > 0 ? "Confirm & Continue to Deposit" : "Confirm Booking"}
                  </button>
                ) : (
                  <div className="mt-6">
                    <p className="text-xs font-mono text-emerald-400 mb-3" data-testid="booking-saved">
                      Booking saved — {booking?.payment_method === "pay_now" ? `complete your $${deposit.toLocaleString()} payment` : `pay your $${deposit} deposit`} to lock the slot:
                    </p>
                    <DepositPayPal booking={booking} onPaid={() => setDone(true)} />
                  </div>
                )}
              </div>
            )}

            {/* nav */}
            <div className="flex justify-between mt-8">
              <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1 || !!booking}
                data-testid="book-back"
                className="px-5 py-2.5 rounded border border-zinc-700 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:border-[#d4af37] transition-colors disabled:opacity-30">
                ← Back
              </button>
              {step < 6 && (
                <button type="button" onClick={() => setStep((s) => s + 1)} disabled={nextDisabled}
                  data-testid="book-next"
                  className="px-6 py-2.5 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity disabled:opacity-40">
                  Continue →
                </button>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
